// ============================================================================
// STUDIO CANETTE — CLIENT POCKETBASE (NAVIGATEUR)
//
// Un seul client pour toute l'application. Deux instances qui persistent leur
// jeton sous la même clé de stockage finissent par se le voler : l'une écrit un
// jeton frais, l'autre garde l'ancien en mémoire et déconnecte l'utilisateur au
// premier appel. Ici, une instance, une clé, un point d'entrée.
//
// Le navigateur ne parle à PocketBase QUE pour l'authentification
// (ARCHITECTURE.md § 5.2 : toutes les collections métier ont leurs règles à
// `null`, seule l'API Express y accède en superutilisateur). Le jeton produit
// ici part ensuite en `Authorization: Bearer` vers /api/* — c'est tout son rôle.
//
// Trois décisions qui méritent une ligne :
//
//  • CLÉ DE STOCKAGE EXPLICITE. En production, PocketBase peut être servi sur
//    la même origine que le front derrière un reverse proxy (VITE_PB_URL=/pb) —
//    et son interface d'administration utilise elle aussi `localStorage`. Une
//    clé propre à l'application évite la collision, et rend le nettoyage lisible
//    dans les outils de développement.
//
//  • URL RELATIVE ACCEPTÉE. Le SDK résout une `baseURL` commençant par « / »
//    contre l'origine courante. C'est le mode de déploiement à un seul nom de
//    domaine, sans CORS ni cookie tiers : on le prend en charge.
//
//  • AUTO-ANNULATION LAISSÉE PAR DÉFAUT. Le SDK annule les requêtes identiques
//    encore en vol. Côté serveur c'est un piège (voir pb/API-JSVM.md), côté
//    navigateur on ne fait que de l'authentification : les rares appels
//    concurrents (`authRefresh`) sont dédupliqués dans session.js, et une
//    requête abandonnée y est traitée comme un incident réseau — jamais comme
//    une session morte.
//
// Mode démo : sans VITE_PB_URL, on n'échoue pas au chargement. `hasPB` vaut
// faux et `pb` est un objet factice dont chaque méthode rejette avec un message
// explicite. Un contributeur peut cloner le dépôt, lancer `npm run dev` sans
// `.env` et voir la landing, les tarifs et les pages légales.
// ============================================================================

import PocketBase, { LocalAuthStore } from 'pocketbase';

/* ------------------------------------------------------------------ *
 * Configuration                                                       *
 * ------------------------------------------------------------------ */

const RAW = String(import.meta.env?.VITE_PB_URL ?? '').trim();

// Pas de slash final : le SDK concatène des chemins derrière (`/api/collections/…`)
// et « http://x:8090//api » n'est pas la même route que « http://x:8090/api ».
// Seul cas conservé tel quel : « / », qui désigne l'origine courante.
const URL_ = RAW === '/' ? '/' : RAW.replace(/\/+$/, '');

/** Absolue (http/https) ou relative à l'origine — les deux se déploient. */
const VALIDE = /^https?:\/\/[^\s/]+/i.test(URL_) || URL_.startsWith('/');

/** Faux si VITE_PB_URL manque ou est inexploitable : l'application est en mode démo. */
export const hasPB = VALIDE;

/** Clé `localStorage`. Voir le bandeau : elle est volontairement à nous. */
const STORAGE_KEY = 'sc_auth';

const DEMO_MSG = "PocketBase n'est pas configuré : renseignez VITE_PB_URL dans .env.";

/* ------------------------------------------------------------------ *
 * Client factice (mode démo)                                          *
 * ------------------------------------------------------------------ */

/**
 * Même forme qu'une `ClientResponseError` du SDK : `status`, `response`, `data`.
 * Les traductions d'erreur de session.js lisent ces champs sans avoir à savoir
 * si le client est réel ou factice.
 */
class PBNotConfiguredError extends Error {
  constructor() {
    super(DEMO_MSG);
    this.name = 'PBNotConfiguredError';
    this.url = '';
    this.status = 0;
    this.isAbort = false;
    this.response = { code: 0, message: DEMO_MSG, data: {} };
    this.originalError = null;
  }
  get data() { return this.response; }
}

/** Toute méthode du client factice échoue de la même façon, et le dit. */
const refus = () => Promise.reject(new PBNotConfiguredError());

const demoAuthStore = {
  token: '',
  record: null,
  model: null,
  isValid: false,
  isSuperuser: false,
  save() {},
  clear() {},
  // Même signature que BaseAuthStore.onChange : l'appelant reçoit toujours une
  // fonction de désabonnement, il n'a pas à tester le mode démo.
  onChange(cb, fireImmediately = false) {
    if (fireImmediately && typeof cb === 'function') {
      try { cb('', null); } catch { /* l'abonné a lâché : ce n'est pas notre affaire */ }
    }
    return () => {};
  }
};

const demoCollection = {
  // Authentification
  authWithPassword: refus,
  authWithOAuth2: refus,
  authWithOAuth2Code: refus,
  authRefresh: refus,
  listAuthMethods: refus,
  requestVerification: refus,
  confirmVerification: refus,
  requestPasswordReset: refus,
  confirmPasswordReset: refus,
  requestEmailChange: refus,
  confirmEmailChange: refus,
  requestOTP: refus,
  authWithOTP: refus,
  // CRUD : le navigateur n'y touche pas (collections fermées), mais un appel
  // égaré doit expliquer pourquoi il échoue plutôt que planter sur « … is not
  // a function ».
  create: refus,
  update: refus,
  delete: refus,
  getOne: refus,
  getList: refus,
  getFullList: refus,
  getFirstListItem: refus,
  subscribe: refus,
  unsubscribe: async () => {}
};

const demoClient = {
  baseURL: '',
  authStore: demoAuthStore,
  collection: () => demoCollection,
  autoCancellation() { return this; },
  cancelAllRequests() { return this; },
  cancelRequest() { return this; },
  filter: raw => String(raw ?? ''),
  files: { getURL: () => '', getUrl: () => '', getToken: refus },
  health: { check: refus },
  send: refus
};

/* ------------------------------------------------------------------ *
 * Le client                                                           *
 * ------------------------------------------------------------------ */

/** Le client, réel ou factice — l'appelant n'a pas à faire la différence. */
export const pb = VALIDE
  ? new PocketBase(URL_, new LocalAuthStore(STORAGE_KEY))
  : demoClient;

if (!VALIDE && import.meta.env?.DEV) {
  console.warn(`[studiocanette] ${DEMO_MSG} L'application tourne en mode démo (pages publiques seulement).`);
}
